import { PrimaryButton, WhiteButton } from '../../ui/Button/Buttons';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { TutorialGuideProgressBar } from './TutorialGuideProgressBar';
import { CheckmarkIcon, CloseIconWhite } from '../../../icons/SvgIcons';
import { TUTORIAL_GUIDE_STEPS } from './TutorialGuide.constants';
import {
  closeTutorialGuide,
  getTutorialGuideProgressPercentage,
  setCurrentTutorialGuideStep
} from '../../../store/slices/tutorialGuideSlice';
import { useNavigate } from 'react-router-dom';
import {
  setEnableStepper,
  setShowPreStep
} from '../../../store/slices/stepperSlice';
import {
  abandOnboarding,
  changeOnboardingStep,
  completeOnboarding
} from '../../../services/OnboardingService';
import { useState } from 'react';
import { GuideCompletionMethod } from '../../../types/OnboardingGuide.types';

// Create reusable fucntion for this
const getStepperItemConfig = (
  status: 'COMPLETED' | 'IN_PROGRESS' | 'UPCOMING'
) => {
  switch (status) {
    case 'COMPLETED':
      return {
        numberClass: 'bg-purple-600 text-white'
      };
    case 'IN_PROGRESS':
      return {
        numberClass:
          'border border-purple-600 bg-white text-purple-600 font-semibold'
      };
    case 'UPCOMING':
    default:
      return {
        numberClass: 'border border-gray-200 text-gray-550 font-medium'
      };
  }
};

export function TutorialGuideModal() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [disableButton, setDisableButton] = useState(false);
  const { isOpen, currentStepKey, sessionId } = useAppSelector(
    (state) => state.tutorialGuide
  );

  const currentStepIndex = TUTORIAL_GUIDE_STEPS.findIndex(
    (step) => step.key === currentStepKey
  );
  const currentStep = TUTORIAL_GUIDE_STEPS[currentStepIndex];

  const progressPercentage = getTutorialGuideProgressPercentage(
    currentStepIndex + 1
  );
  const showBackButton = currentStep.key !== 'DASHBOARD_OVERVIEW';
  const currentStepperItem = currentStep.stepperItems?.find(
    (item) => item.status === 'IN_PROGRESS'
  );
  const nextStep = TUTORIAL_GUIDE_STEPS[currentStepIndex + 1];
  const previousStep = TUTORIAL_GUIDE_STEPS[currentStepIndex - 1];
  const lastStep = currentStep.key === 'LIFECYCLE_PHASE_6';
  const firstStep = currentStep.key === 'WELCOME';

  const handleCompleteOnboarding = async (method: GuideCompletionMethod) => {
    try {
      await completeOnboarding(sessionId!, method);
    } catch (error) {
      console.error('Error completing onboarding guide:', error);
    } finally {
      setDisableButton(false);
    }

    if (method === 'CTA_CLICK') {
      dispatch(setEnableStepper(true));
      dispatch(setShowPreStep(true));
      dispatch(closeTutorialGuide());
      navigate('/questionnaire');
    }

    dispatch(closeTutorialGuide());
  };

  const handleClose = async () => {
    setDisableButton(true);

    if (lastStep) {
      handleCompleteOnboarding('X_CLICK');
      return;
    }

    try {
      await abandOnboarding(sessionId!);
    } catch (error) {
      console.error('Error abandoning onboarding guide:', error);
    } finally {
      setDisableButton(false);
    }
    dispatch(closeTutorialGuide());
  };

  const handleNextButton = async () => {
    setDisableButton(true);

    if (lastStep) {
      handleCompleteOnboarding('CTA_CLICK');
      return;
    }

    try {
      await changeOnboardingStep(sessionId!, nextStep.key);
      dispatch(setCurrentTutorialGuideStep(nextStep.key));
    } catch (error) {
      console.error('Error changing onboarding step:', error);
    } finally {
      setDisableButton(false);
    }
  };

  const handleBackButton = async () => {
    setDisableButton(true);

    if (firstStep) {
      handleClose();
      return;
    }

    try {
      await changeOnboardingStep(sessionId!, previousStep.key);
      dispatch(setCurrentTutorialGuideStep(previousStep.key));
    } catch (error) {
      console.error('Error changing onboarding step:', error);
    } finally {
      setDisableButton(false);
    }
  };

  if (!isOpen || !currentStep) return null;

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center">
      <div className="absolute inset-0 bg-gray-950/66" aria-hidden="true" />
      <div
        key={currentStep.key}
        role="dialog"
        aria-modal="true"
        className={`rounded-[8px] rounded-t-[4px] bg-white shadow-[0_20px_25px_-5px_rgba(0,0,0,0.10),_0_10px_10px_-5px_rgba(0,0,0,0.04)] ${currentStep.modalClassName ?? 'relative flex max-h-[calc(100vh-48px)] w-full max-w-[660px] flex-col overflow-visible'}`}
      >
        {/* Arrow */}
        {currentStep.showArrow && (
          <div
            className="absolute -left-3 top-8 w-[12px] h-[24px] bg-white"
            style={{
              clipPath: 'polygon(100% 0%, 0% 50%, 100% 100%)'
            }}
          />
        )}

        <TutorialGuideProgressBar progressPercentage={progressPercentage} />

        <button
          type="button"
          onClick={handleClose}
          id="close-tutorial-guide"
          aria-label="Close tutorial modal"
          className="absolute top-0 -right-[32px] z-10 h-6 w-6 flex items-center justify-center rounded-md bg-[rgba(247,247,248,0.25)]"
        >
          {CloseIconWhite()}
        </button>

        <div className="flex flex-col gap-6 overflow-y-auto p-6 text-left">
          <div className="flex flex-col gap-2">
            <h2 className="text-xl leading-8 font-semibold text-gray-900">
              {currentStep.icon ? (
                <span className="flex items-center gap-1.5">
                  <span className="mb-1">{currentStep.icon}</span>
                  {currentStep.title}
                </span>
              ) : (
                currentStep.title
              )}
            </h2>
            <div className="text-sm text-gray-800 leading-5 space-y-5">
              {currentStep.description}
            </div>
          </div>

          {/* Stepper */}
          {currentStep.stepperItems && (
            <div className="flex items-start justify-between gap-2">
              {currentStep.stepperItems.map((item, index) => {
                const config = getStepperItemConfig(item.status);
                const isLastItem =
                  index === currentStep.stepperItems!.length - 1;

                return (
                  <div key={item.id} className="flex items-center gap-2">
                    <div className="flex flex-col items-center gap-2 relative shrink-0">
                      {item.status === 'COMPLETED' ? (
                        <div className="w-6 h-6 flex items-center justify-center bg-purple-600 rounded-full">
                          {CheckmarkIcon()}
                        </div>
                      ) : (
                        <div
                          className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${config.numberClass}`}
                        >
                          {item.id}
                        </div>
                      )}
                    </div>
                    {!isLastItem && (
                      <div className="h-[2px] w-[77px] bg-gray-250v2"></div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Title and desc for current step */}
          <div className="flex max-h-[430px] flex-col gap-6 overflow-y-auto overflow-x-hidden">
            {currentStepperItem && (
              <div className="flex flex-col gap-2">
                <h3 className="text-base font-medium text-gray-900">
                  {currentStepperItem.title}
                </h3>
                <p className="text-sm text-gray-800 leading-5 min-h-10">
                  {currentStepperItem.description}
                </p>
              </div>
            )}

            {/* Visual */}
            {currentStepperItem && (
              <div className="w-[612px] h-[334px] shrink-0 overflow-hidden">
                {currentStepperItem.visual.endsWith('.mp4') ? (
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="scale-[1.02]"
                  >
                    <source src={currentStepperItem.visual} type="video/mp4" />
                  </video>
                ) : (
                  <img
                    src={currentStepperItem.visual}
                    alt={currentStepperItem.title}
                  />
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 h-[38px]">
            {showBackButton && (
              <WhiteButton
                onClick={handleBackButton}
                disabled={disableButton}
                className="text-gray-800"
              >
                {currentStep.backButtonLabel}
              </WhiteButton>
            )}
            <PrimaryButton
              onClick={handleNextButton}
              disabled={disableButton}
              className={currentStep.nextButtonClassName}
            >
              {currentStep.nextButtonIcon && (
                <span className="mr-2 mb-0.5">
                  {currentStep.nextButtonIcon}
                </span>
              )}
              {currentStep.nextButtonLabel}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
}
